// v3.15-914: Funded But Active — admin reconciliation page.
// Lists deals where ANY payment marker is set (so they ARE funded per is_deal_funded())
// but their deal_stage is still in an "active" state. One-click "Close" per row + bulk
// "Close all" that flips them to deal_stage=funded / funding_status=funded / is_active=false.
// Mirrors the FundedMismatchesPage layout for consistency.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2, RefreshCw, AlertTriangle, ExternalLink, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatET } from '../utils/timeUtils';

const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const getPaidDate = (row) => (
  row?.vendor_payment_received_date ||
  row?.ptc_paid_date ||
  row?.partner_paid_date ||
  row?.ptc_payment_received_date ||
  row?.funded_date ||
  ''
);

const getRowAmount = (row) => row?.funded_amount ?? row?.approved_amount ?? row?.deal_amount ?? 0;

export default function FundedButActivePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [closingIds, setClosingIds] = useState(() => new Set());
  const [closingAll, setClosingAll] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/funded-but-active');
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load funded-but-active deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const closeOne = async (row) => {
    setClosingIds((prev) => { const n = new Set(prev); n.add(row.id); return n; });
    try {
      await api.post(`/admin/funded-but-active/${row.id}/close`, {});
      toast.success(`${row.full_name || row.id} → moved to Closed/Funded`);
      setItems((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to close deal');
    } finally {
      setClosingIds((prev) => { const n = new Set(prev); n.delete(row.id); return n; });
    }
  };

  const closeAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Close all ${items.length} funded-but-active deals? This sets deal_stage=funded and is_active=false. Audit log captures every change.`)) return;
    setClosingAll(true);
    try {
      const res = await api.post('/admin/funded-but-active/close-all', {});
      const closed = res.data?.closed || 0;
      const failed = (res.data?.failed || []).length;
      toast.success(`Closed ${closed} deal${closed === 1 ? '' : 's'}${failed ? ` — ${failed} failed` : ''}`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk close failed');
    } finally {
      setClosingAll(false);
    }
  };

  return (
    <div className="space-y-4 p-6" data-testid="funded-but-active-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            Funded but stuck in Active
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Deals where a payment marker exists (so they ARE funded) but <code>deal_stage</code> is still active.
            Click <strong>Close</strong> to flip stage → <code>funded</code>, status → <code>funded</code>,
            <code> is_active=false</code>. Every change is audit-logged.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchAll} variant="outline" disabled={loading} data-testid="refresh-funded-active-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
          <Button
            onClick={closeAll}
            disabled={closingAll || items.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="close-all-funded-active-btn"
          >
            {closingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Close all ({items.length})
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-sm text-slate-500">No funded-but-active deals. Every paid deal is properly closed.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50/70">
                  <tr className="border-b border-slate-200">
                    {['Deal', 'Stage', 'Status', 'Amount', 'Paid Date', 'Rep', 'Last Updated', 'Actions'].map((h) => (
                      <th key={h} className={`py-2 px-3 text-[11px] font-medium text-slate-500 uppercase tracking-wider ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} className="border-b border-slate-100 hover:bg-amber-50/40" data-testid={`funded-active-row-${row.id}`}>
                      <td className="py-2 px-3">
                        <div className="font-medium text-slate-800">{row.full_name || '—'}</div>
                        <div className="text-[10px] font-mono text-slate-400">{row.id}</div>
                      </td>
                      <td className="py-2 px-3"><Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50">{row.deal_stage || '—'}</Badge></td>
                      <td className="py-2 px-3"><Badge variant="outline">{row.funding_status || '—'}</Badge></td>
                      <td className="py-2 px-3 font-semibold text-emerald-700">{fmtMoney(getRowAmount(row))}</td>
                      <td className="py-2 px-3 text-xs text-slate-600">{getPaidDate(row) || '—'}</td>
                      <td className="py-2 px-3 text-xs text-slate-600">{row.sales_rep_name || row.ptc_rep_name || '—'}</td>
                      <td className="py-2 px-3 text-xs text-slate-500">{row.updated_at ? formatET(row.updated_at, 'MMM d, h:mm a') : '—'}</td>
                      <td className="py-2 px-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-7 px-2 text-xs"
                            onClick={() => closeOne(row)}
                            disabled={closingIds.has(row.id)}
                            data-testid={`close-funded-active-${row.id}`}
                          >
                            {closingIds.has(row.id) ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                            Close
                          </Button>
                          <Link
                            to={`/clients/${row.id}`}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs"
                            data-testid={`open-funded-active-${row.id}`}
                          >
                            Open <ExternalLink className="h-3 w-3" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
